import {
  CheckCircleOutline,
  ErrorOutline,
  HourglassTop,
  Refresh,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useOrganization } from "../contexts/OrganizationContext";
import { checkoutApi } from "../services/checkout-service";
import { organizationApi } from "../services/organization-service";
import {
  clearActiveCheckout,
  normalizeCheckoutStatus,
  readActiveCheckout,
} from "../utils/active-checkout";

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 20;

const getViewState = (status) => {
  if (status === "paid") {
    return {
      color: "success",
      icon: CheckCircleOutline,
      titleKey: "moyasarCallback.successTitle",
      descriptionKey: "moyasarCallback.successDescription",
    };
  }

  if (status === "failed" || status === "cancelled" || status === "expired") {
    return {
      color: "error",
      icon: ErrorOutline,
      titleKey: `moyasarCallback.${status}Title`,
      descriptionKey: `moyasarCallback.${status}Description`,
    };
  }

  if (status === "pending" || status === "processing" || status === "verifying") {
    return {
      color: "warning",
      icon: HourglassTop,
      titleKey: "moyasarCallback.pendingTitle",
      descriptionKey:
        status === "processing"
          ? "moyasarCallback.provisioningDescription"
          : "moyasarCallback.pendingDescription",
    };
  }

  return {
    color: "error",
    icon: ErrorOutline,
    titleKey:
      status === "timeout"
        ? "moyasarCallback.timeoutTitle"
        : "moyasarCallback.verificationErrorTitle",
    descriptionKey:
      status === "mismatch"
        ? "moyasarCallback.sessionMismatchDescription"
        : status === "timeout"
          ? "moyasarCallback.timeoutDescription"
          : "moyasarCallback.verificationErrorDescription",
  };
};

const MoyasarPaymentCallback = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { setOrganization } = useOrganization();
  const [searchParams] = useSearchParams();
  const storedCheckout = useMemo(readActiveCheckout, []);
  const urlSessionId = searchParams.get("session_id")?.trim() || null;
  const sessionId = urlSessionId || storedCheckout?.sessionId || null;
  const hasSessionMismatch = Boolean(
    urlSessionId &&
      storedCheckout?.sessionId &&
      urlSessionId !== storedCheckout.sessionId,
  );
  const [status, setStatus] = useState(
    hasSessionMismatch ? "mismatch" : "verifying",
  );
  const [backendStatus, setBackendStatus] = useState("");
  const timerRef = useRef(null);
  const navigationTimerRef = useRef(null);
  const abortRef = useRef(null);
  const requestInFlightRef = useRef(false);
  const pollingAttemptsRef = useRef(0);
  const successHandledRef = useRef(false);
  const verifyRef = useRef(null);
  const isDarkMode = theme.palette.mode === "dark";

  const clearTimers = useCallback(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    if (navigationTimerRef.current) {
      window.clearTimeout(navigationTimerRef.current);
    }
    timerRef.current = null;
    navigationTimerRef.current = null;
  }, []);

  useEffect(() => {
    let disposed = false;

    const scheduleNextVerification = () => {
      if (disposed) return;
      if (pollingAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
        setStatus("timeout");
        return;
      }

      timerRef.current = window.setTimeout(() => {
        verifyRef.current?.();
      }, POLL_INTERVAL_MS);
    };

    const handlePaidCheckout = async (orgId, signal) => {
      if (successHandledRef.current || disposed) return;

      if (!orgId) {
        setStatus("processing");
        scheduleNextVerification();
        return;
      }

      try {
        const organizationResponse = await organizationApi.getOrganization(
          orgId,
          { signal, timeout: 15000 },
        );
        if (disposed || !organizationResponse?.data) return;

        successHandledRef.current = true;
        setOrganization(organizationResponse.data);
        clearActiveCheckout();
        setStatus("paid");
        navigationTimerRef.current = window.setTimeout(() => {
          navigate("/settings?tab=organizations", { replace: true });
        }, 1200);
      } catch (error) {
        if (error?.name === "CanceledError" || error?.name === "AbortError") {
          return;
        }
        if (!disposed) {
          setStatus("processing");
          scheduleNextVerification();
        }
      }
    };

    const verifyCheckout = async () => {
      if (disposed || requestInFlightRef.current) return;
      clearTimers();

      if (hasSessionMismatch) {
        setStatus("mismatch");
        return;
      }
      if (!storedCheckout?.paymentSubmitted) {
        clearActiveCheckout();
        navigate("/settings?tab=organizations&checkout=open", {
          replace: true,
        });
        return;
      }
      if (!sessionId) {
        setStatus("error");
        return;
      }

      requestInFlightRef.current = true;
      pollingAttemptsRef.current += 1;
      abortRef.current = new AbortController();

      try {
        const response = await checkoutApi.getCheckout(
          sessionId,
          abortRef.current.signal,
        );
        if (disposed) return;

        if (
          typeof response?.status !== "string" ||
          typeof response?.action !== "string" ||
          (response?.org_id !== null && typeof response?.org_id !== "string")
        ) {
          setStatus("unknown");
          return;
        }

        const normalizedStatus = normalizeCheckoutStatus(response);
        setBackendStatus(response.status);

        if (normalizedStatus === "pending" || normalizedStatus === "processing") {
          setStatus(normalizedStatus);
          scheduleNextVerification();
        } else if (normalizedStatus === "paid") {
          await handlePaidCheckout(response.org_id, abortRef.current.signal);
        } else if (normalizedStatus === "failed") {
          setStatus("failed");
          navigate(
            "/settings?tab=organizations&checkout=open&payment=failed",
            { replace: true },
          );
        } else if (normalizedStatus === "cancelled") {
          setStatus("cancelled");
          navigate(
            "/settings?tab=organizations&checkout=open&payment=cancelled",
            { replace: true },
          );
        } else if (normalizedStatus === "expired") {
          clearActiveCheckout();
          setStatus("expired");
          navigate(
            "/settings?tab=organizations&checkout=open&payment=expired",
            { replace: true },
          );
        } else {
          console.warn("Unknown checkout status received", {
            sessionId,
            status: response.status,
          });
          setStatus("unknown");
        }
      } catch (error) {
        if (
          !disposed &&
          error?.name !== "CanceledError" &&
          error?.name !== "AbortError"
        ) {
          setStatus("error");
        }
      } finally {
        requestInFlightRef.current = false;
        abortRef.current = null;
      }
    };

    verifyRef.current = verifyCheckout;
    pollingAttemptsRef.current = 0;
    successHandledRef.current = false;
    verifyCheckout();

    return () => {
      disposed = true;
      clearTimers();
      abortRef.current?.abort();
      abortRef.current = null;
      requestInFlightRef.current = false;
      verifyRef.current = null;
    };
  }, [
    clearTimers,
    hasSessionMismatch,
    navigate,
    sessionId,
    setOrganization,
    storedCheckout,
  ]);

  const handleRetryVerification = () => {
    clearTimers();
    pollingAttemptsRef.current = 0;
    setStatus("verifying");
    verifyRef.current?.();
  };

  const callbackState = getViewState(status);
  const StatusIcon = callbackState.icon;
  const isLoading = ["verifying", "pending", "processing"].includes(status);
  const canRetry = Boolean(
    sessionId &&
      !hasSessionMismatch &&
      ["error", "unknown", "timeout"].includes(status),
  );
  const canReturnToCheckout = status === "mismatch" || !sessionId;
  const canLeaveWhilePending = Boolean(
    sessionId &&
      !hasSessionMismatch &&
      ["timeout", "error", "unknown"].includes(status),
  );

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        py: 5,
        color: "text.primary",
        backgroundColor: "background.default",
        backgroundImage: `radial-gradient(circle at 50% 100%, ${
          isDarkMode ? "rgba(45, 212, 191, 0.12)" : "rgba(37, 99, 235, 0.14)"
        }, transparent 42%)`,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 520,
          p: { xs: 3, sm: 5 },
          textAlign: "center",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 4,
          bgcolor: "background.paper",
          boxShadow: isDarkMode
            ? "0 24px 60px rgba(0, 0, 0, 0.36)"
            : "0 24px 60px rgba(15, 23, 42, 0.12)",
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            mx: "auto",
            mb: 3,
            display: "grid",
            placeItems: "center",
            borderRadius: "50%",
            bgcolor: `${callbackState.color}.light`,
            color: `${callbackState.color}.dark`,
          }}
        >
          {isLoading ? (
            <CircularProgress color={callbackState.color} size={42} />
          ) : (
            <StatusIcon sx={{ fontSize: 44 }} />
          )}
        </Box>

        <Typography component="h1" variant="h4" fontWeight={750} gutterBottom>
          {t(callbackState.titleKey)}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t(callbackState.descriptionKey)}
        </Typography>

        {backendStatus && (
          <Chip
            label={t("moyasarCallback.status", { status: backendStatus })}
            color={callbackState.color}
            variant="outlined"
            sx={{ mb: 3, fontWeight: 700, textTransform: "capitalize" }}
          />
        )}

        {sessionId && (
          <Box
            sx={{
              mb: canRetry ? 3 : 0,
              p: 2,
              textAlign: "start",
              borderRadius: 2,
              bgcolor: "action.hover",
              overflowWrap: "anywhere",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t("moyasarCallback.sessionId")}
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {sessionId}
            </Typography>
          </Box>
        )}

        {canRetry && (
          <Button
            onClick={handleRetryVerification}
            fullWidth
            variant="contained"
            size="large"
            startIcon={<Refresh />}
            sx={{ borderRadius: 2, py: 1.25, textTransform: "none", fontWeight: 700 }}
          >
            {t("moyasarCallback.retryVerification")}
          </Button>
        )}

        {canLeaveWhilePending && (
          <Button
            onClick={() => navigate("/", { replace: true })}
            fullWidth
            variant="outlined"
            size="large"
            sx={{
              mt: 1.5,
              borderRadius: 2,
              py: 1.25,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            {t("moyasarCallback.goToDashboard")}
          </Button>
        )}

        {canReturnToCheckout && (
          <Button
            onClick={() =>
              navigate("/settings?tab=organizations&checkout=open", {
                replace: true,
              })
            }
            fullWidth
            variant="contained"
            size="large"
            sx={{ borderRadius: 2, py: 1.25, textTransform: "none", fontWeight: 700 }}
          >
            {t("moyasarCallback.returnToCheckout")}
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default MoyasarPaymentCallback;
